export interface AgeGateClientSnapshot {
	acted: boolean
	isAdult: boolean | null
	showModal: boolean
	pendingRedirect: boolean
}

declare global {
	interface Window {
		__age_gate_state__?: AgeGateClientSnapshot
		__age_modal_acted__?: boolean
		__age_pending_redirect__?: boolean
	}
}

const cookieToAdultState = (cookieValue: boolean | null | undefined) => {
	if (cookieValue === true) return true
	if (cookieValue === false) return false
	return null
}

/**
 * Reads the current age-gate state from the in-memory window flags set by the age-gate modal.
 * Returns `null` on the server or when the user has not yet interacted with the modal.
 * @param cookieValue - Current value of the `is_adult` cookie, used as fallback when the
 *   full `__age_gate_state__` object is absent.
 */
export const getClientAgeGateSnapshot = (cookieValue?: boolean | null): AgeGateClientSnapshot | null => {
	if (!import.meta.client) return null

	if (window.__age_gate_state__) {
		return { ...window.__age_gate_state__ }
	}

	if (!window.__age_modal_acted__) return null

	return {
		acted: true,
		isAdult: window.__age_pending_redirect__ ? false : cookieToAdultState(cookieValue),
		showModal: false,
		pendingRedirect: !!window.__age_pending_redirect__,
	}
}

/**
 * Derives the age-gate UI state (`isAdult`, `showModal`) by combining the in-memory client
 * snapshot (preferred) with the persistent cookie value (fallback).
 * @param cookieValue - Stored adult-status from the `is_adult` cookie.
 */
export const resolveAgeGateState = (cookieValue: boolean | null | undefined) => {
	const clientSnapshot = getClientAgeGateSnapshot(cookieValue)
	if (clientSnapshot) {
		return {
			isAdult: clientSnapshot.isAdult,
			showModal: clientSnapshot.showModal,
		}
	}

	const isAdult = cookieToAdultState(cookieValue)

	return {
		isAdult,
		showModal: isAdult === null,
	}
}
